import * as B from '@blueprintjs/core';
import React from 'react';

import {usePushNotification} from 'app/components/Notification';

import {sceneCountLabel} from './format';
import {CartItemV1} from './types';
import {CartItemRestore, useCart} from './useCart';

/** The undo window. */
const UNDO_TOAST_DURATION_MS = 5000;

/** Stable key so batched removals update the same toast in place instead of replacing it. */
export const UNDO_TOAST_KEY = 'cart-remove-undo';

export type RemoveWithUndoFn = (item: CartItemV1) => void;

interface PendingUndo {
  /** In removal order, each index as of its own removal: restoreItems replays them backward. */
  entries: CartItemRestore[];
  /** The cart these entries came out of. Restoring into a different one would be someone else's. */
  scopeKey: string | null;
}

const emptyPending = (scopeKey: string | null): PendingUndo => ({entries: [], scopeKey});

const RemoveWithUndoContext = React.createContext<RemoveWithUndoFn | null>(null);

const toastMessage = (entries: CartItemRestore[]): string => {
  const name = entries.length === 1 && entries[0].item.snapshot.featureName;
  return `Removed ${name || sceneCountLabel(entries.length)}`;
};

/**
 * Removes cart items with one app-wide undo toast behind them. Removals that land while the toast
 * is up join the same batch, so a run of clicks is one action to take back rather than several —
 * and one Undo returns every scene in it, to the slot it held.
 */
export const CartUndoProvider: React.FC<React.PropsWithChildren> = ({children}) => {
  const {removeItems, restoreItems, scopeKey} = useCart();
  const pushNotification = usePushNotification();

  const pending = React.useRef<PendingUndo>(emptyPending(scopeKey));

  // Bumped on every toast shown. A dismissal only retires the buffer if its revision is still
  // current: an unrelated toast's teardown clears *all* toasts, firing onDismiss on the undo toast
  // while the batch behind it is still growing.
  const revision = React.useRef(0);

  React.useEffect(() => {
    // A swapped cart is not the one these removals came out of, so the batch retires with it.
    // This is what makes a stale toast's Undo a no-op: its closure and its buffer carry the same
    // old scope, so only retiring the buffer at the swap can tell them apart.
    pending.current = emptyPending(scopeKey);
  }, [scopeKey]);

  const undo = React.useCallback(() => {
    const buffer = pending.current;

    // A click from a toast pushed against a cart that has since been swapped is not this cart's
    // undo, and taking the buffer with it would strand the removals the current cart is still
    // offering back.
    if (buffer.scopeKey !== scopeKey) {
      return;
    }

    // Retired up front, so the dismissal Blueprint fires straight behind this click has nothing
    // left to take and a second click lands on an empty buffer.
    pending.current = emptyPending(scopeKey);

    if (buffer.entries.length === 0) {
      return;
    }

    // The buffer stays in removal order: restoreItems replays it newest-first, which is what makes
    // every splice land on the list its index was captured against.
    const skippedFull = restoreItems(buffer.entries);

    // Duplicates stay quiet: the scene is in the cart, which is all the undo was asking for.
    if (skippedFull > 0) {
      pushNotification({
        message: `Cart is full — ${sceneCountLabel(skippedFull)} couldn't be restored.`,
        autoHideDuration: UNDO_TOAST_DURATION_MS,
      });
    }
  }, [pushNotification, restoreItems, scopeKey]);

  const showUndoToast = React.useCallback(
    (entries: CartItemRestore[]) => {
      revision.current++;
      const shown = revision.current;
      pushNotification({
        message: toastMessage(entries),
        autoHideDuration: UNDO_TOAST_DURATION_MS,
        key: UNDO_TOAST_KEY,
        // The key above is what makes this an in-place update: Blueprint matches it to the toast
        // that's already showing, so it grows to the new count with no exit/entry animation.
        options: {
          // Blueprint's native action, which dismisses the toast on click — nothing here has to.
          // The Toast strips `intent` from its action, so the intent class is what makes Undo
          // read as primary.
          action: {
            text: 'Undo',
            onClick: undo,
            className: B.Classes.intentClass(B.Intent.PRIMARY),
          },
          onDismiss: () => {
            if (revision.current === shown) {
              pending.current = emptyPending(scopeKey);
            }
          },
        },
      });
    },
    [pushNotification, scopeKey, undo]
  );

  const removeWithUndo = React.useCallback<RemoveWithUndoFn>(
    (item) => {
      // The scope effect above retires a swapped cart's batch, but it runs after the commit that
      // handed this callback out. Re-checking here keeps a click that beats the effect from
      // folding the old cart's removals into this cart's undo.
      if (pending.current.scopeKey !== scopeKey) {
        pending.current = emptyPending(scopeKey);
      }

      const removed = removeItems([item.id]);
      // Nothing removed: the item isn't in the cart.
      if (removed.length === 0) {
        return;
      }

      pending.current = {
        scopeKey,
        entries: [...pending.current.entries, ...removed],
      };
      showUndoToast(pending.current.entries);
    },
    [removeItems, scopeKey, showUndoToast]
  );

  return (
    <RemoveWithUndoContext.Provider value={removeWithUndo}>
      {children}
    </RemoveWithUndoContext.Provider>
  );
};

/**
 * Falls back to a plain removal when no CartUndoProvider is mounted, the way CartContext falls back
 * to an inert cart: rows render in logged-out trees and tests that never mount either provider.
 */
export const useRemoveWithUndo = (): RemoveWithUndoFn => {
  const removeWithUndo = React.useContext(RemoveWithUndoContext);
  const {removeItem} = useCart();

  return React.useMemo(
    () => removeWithUndo ?? ((item: CartItemV1) => removeItem(item.id)),
    [removeItem, removeWithUndo]
  );
};
